import Utils from "../../libs/Utils"
import createSpline, { type TDataPoint } from "./Spline"

export type TChartLabel = (val:number) => string
export type TXAxis = number|Date
export type TLineChartData = [TXAxis,number]

const DENOM :[number,string][] = [[1_000_000_000, "B"], [1_000_000, "M"], [1_000, "K"]]
const LABEL_Y = 32
const PADDING = 8
const DECIMAL = 2
const INTERVAL = 5

function n2label(value:number) {
    for(const denom of DENOM) {
        const [d,label] = denom
        if (value>d) return (value/d).toFixed(DECIMAL) + label
    }
    return value.toFixed(DECIMAL)
}

function n2str(value:number){
    return value.toFixed(2)
}

export default class LineChartCanvas {
    private _canvas:HTMLCanvasElement
    private _rows:TLineChartData[]
    private _g:CanvasRenderingContext2D|null
    private _canvasWidth:number = 0
    private _canvasHeight:number = 0
    private _gutterLeft:number = 0
    private _gutterBottom:number = 0
    private _graphWidth:number = 0
    private _graphHeight:number = 0
    private _data:TDataPoint[] = []
    private _spine:(x:number) => number = () => 0
    private _idata:ImageData|undefined
    private _valueWidth:number = 0
    private _valueHeight:number = 0
    private _labelWidth:number = 0
    private _minY:number = 0
    private _maxY:number = 0
    private _scaleY:number = 0      // [0..1] => [minY, maxY]

    private _labelY:TChartLabel
    private _value:TChartLabel

    constructor(canvas:HTMLCanvasElement, rows:TLineChartData[], labelY:TChartLabel|undefined, value:TChartLabel|undefined){
        (window as any).c = this
        this._canvas = canvas
        this._rows = rows
        this._labelY = labelY || n2label
        this._value = value || n2str

        const g = this._g = canvas.getContext("2d")        
        if (!g) return 

        // measure gutter
        this.loadFont(g)

        const measure = g.measureText("A")
        this._valueHeight = Math.max(LABEL_Y, Math.ceil(measure.fontBoundingBoxAscent + measure.fontBoundingBoxDescent))
        this._valueWidth = Math.max(...rows.map(row => g.measureText(this._labelY(row[1])).width))
        this._labelWidth = Math.max(...rows.map(row => g.measureText(row[0].toString()).width))

        const rect = canvas.getBoundingClientRect()
        this._canvasWidth = rect.width * window.devicePixelRatio
        this._canvasHeight = rect.height * window.devicePixelRatio
        canvas.width = this._canvasWidth
        canvas.height = this._canvasHeight

        this._gutterBottom = this._labelWidth + PADDING*2
        this._gutterLeft = this._valueWidth + PADDING*2

        this._graphWidth = this._canvasWidth - this._gutterLeft - PADDING
        this._graphHeight = this._canvasHeight - this._gutterBottom - PADDING

        g.clearRect(0, 0, this._canvasWidth, this._canvasHeight)

        this.loadFont(g)

        this._minY = Math.min(...rows.map(d => d[1]))
        this._maxY = Math.max(...rows.map(d => d[1]))
        if (this._maxY- this._maxY<0.00001) {
            this._maxY *= 1.05
            this._minY *= 0.95
        }else{
            const offset = (this._maxY-this._minY)*0.05
            this._maxY += offset
            this._minY = Math.max(0, this._minY - offset)
        }
        const rangeY =  this._maxY - this._minY

        // draw y axis
        const c_axisY = Math.floor(this._graphHeight/this._valueHeight)
        for(var i=0; i<=c_axisY; i++) {
            const text = this._labelY(i/c_axisY * rangeY + this._minY)
            const y = this._graphHeight  + PADDING - i*this._valueHeight
            g.beginPath()
            g.moveTo(this._gutterLeft, y)
            g.lineTo(this._canvasWidth-PADDING, y)
            g.strokeStyle ="#ddd"
            g.lineWidth = 0.5
            g.stroke()

            // g.font = 18*window.devicePixelRatio + "px arial"
            g.textBaseline = "middle"
            g.textAlign = "right"
            g.fillStyle = "#888";
            g.fillText(text, this._gutterLeft - PADDING, y)
        }

        // draw x axis
        const c_axisX = Math.floor(this._graphWidth/this._valueHeight)
        const c_axisXInc = Math.ceil(rows.length/c_axisX)

        g.save()
        g.translate(this._gutterLeft, PADDING*2+this._graphHeight)
        g.rotate(-Math.PI/2)
        for(var i=0; i<rows.length; i+=c_axisXInc) {
            const y = i/rows.length*this._graphWidth
            g.fillText(rows[i][0].toString(), 0, y)
        }
        g.restore()

        g.beginPath()
        g.moveTo(this._gutterLeft-1, PADDING)
        g.lineTo(this._gutterLeft-1, this._canvasHeight-this._gutterBottom+1)
        g.lineTo(this._canvasWidth-PADDING, this._canvasHeight-this._gutterBottom+1)
        g.strokeStyle = "#333"
        g.lineWidth = 2
        g.stroke()

        this._scaleY = rangeY/ this._maxY * this._graphHeight

        const len = rows.length-1
        this._data = rows.map((point,i) => [i, (point[1]-this._minY)/rangeY])
        this._spine = createSpline(this._data, 0.98)

        var i = 0;
        g.beginPath()
        while(i<=this._graphWidth) {
            const y = this._spine(i/this._graphWidth*len)*this._scaleY
            if (i==0) g.moveTo(this._gutterLeft + i, this._graphHeight + PADDING -y)
            else g.lineTo(this._gutterLeft + i, this._graphHeight + PADDING -y)
            if (i==this._graphWidth) break;
            else i = Math.min(i+INTERVAL, this._graphWidth)
        }

        g.strokeStyle = "#10B981";
        g.lineWidth = 2;
        g.stroke()
        this._idata = g.getImageData(0, 0, this._canvasWidth, this._canvasHeight)        
    }

    loadFont(g:CanvasRenderingContext2D) {
        g.font = 16*window.devicePixelRatio + "px arial"
        return g
    }

    clear(){
        if (!this._g || !this._idata) return

        this._g.clearRect(0, 0, this._canvasWidth, this._canvasHeight)
        this._g.putImageData(this._idata, 0, 0)
    }

    hover(e:{clientX:number,clientY:number}) {
        if (!this._g) return

        this.clear()
        const g = this._g
        const rect = this._canvas.getBoundingClientRect()
        const clientX = (e.clientX-rect.left) * window.devicePixelRatio
        const offsetX = (clientX - this._gutterLeft)

        const i = Math.min(Math.max(0, Math.round(offsetX/this._graphWidth*(this._rows.length-1))), this._rows.length-1)
        const x = (i/(this._rows.length-1))*this._graphWidth
        const y = this._graphHeight + PADDING - this._spine(i)*this._scaleY
        const label = this._rows[i][0].toString()
        const value = this._rows[i][1]

        const left = x + this._gutterLeft
        g.save()
        g.beginPath()
        g.ellipse(left, y, 4, 4, Math.PI*2, 0, Math.PI*2)
        g.lineWidth = 0.5
        g.fillStyle = "#aaa"
        g.fill()
        g.restore()

        g.save()
        g.beginPath()
        g.moveTo(this._gutterLeft, y)
        g.lineTo(left, y)
        g.lineTo(left, PADDING+this._graphHeight)
        g.lineWidth = 0.5
        g.strokeStyle = "#aaa"
        g.setLineDash([4, 4])
        g.stroke()
        g.closePath()
        g.restore()

        var top = y
        if (y<this._valueHeight) {
            g.textBaseline = "top"
            top += PADDING
        }
        else {
            g.textBaseline = "bottom"
            top -= PADDING
        }

        g.save()

        const text = `${label} - ${this._value(value)}`
        const textWidth = g.measureText(text).width

        if (left-textWidth/2<this._gutterLeft) g.textAlign = "left"
        else if (left+textWidth/2>this._canvasWidth) g.textAlign = "right"
        else g.textAlign = "center"
        g.fillStyle = "#333";
        g.fillText(text, left, top)
        g.restore()
    }
}